/* ++++++++++ --------------- IMPORTS --------------- ++++++++++ */
// libraries
import React, { useCallback, useState, useMemo, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
// actions 
import { updateAddOnsSetting, createAddOnsSetting } from '../../../../../../state/thunks/commenting';
// material
import { CopySnippetField, InlineMessage, TextField, Grid, GridSet, GridItem } from '@insticator/insticator-ui'; // iui-material
import AddOnController from '../../../../../../material/expandablesection/controllers/addoncontroller/addoncontroller'
import ExpandableSection from '../../../../../../material/expandablesection/expandablesection';
import SectionCard from '../../../../../../material/expandablesection/sectioncard/sectioncard';
import SectionGroup from '../../../../../../material/expandablesection/sectiongroup/sectiongroup';
import SectionGroupDescription from '../../../../../../material/expandablesection/sectiongroupdescription/sectiongroupdescription';
// utils
// styles
import injectSheet from 'react-jss';
import styles from './commentscountersection_styles'

const CommentsCounterSection = (props) => {
  const dispatch = useDispatch();
  const { siteUUID } = useSelector((state) => state.session);
  const isPageIDEnabled = useSelector((state => state.commenting.commentingConfig?.data?.general?.isPageIDEnabled));
  const commentingUnitConfigurationUUID = useSelector(state => state.commenting.commentingConfig?.data?.commentingUnitConfigurationUUID)
  const { commentsCounter } = useSelector(state => state.commenting.commentingAddOns.addOns)

  const [initialFieldsState] = useState({
    zeroCommentsLabel: commentsCounter?.zeroCommentsLabel || '',
    singleCommentLabel: commentsCounter?.singleCommentLabel || '',
    multipleCommentsLabel: commentsCounter?.multipleCommentsLabel || ''
  })

  const [counterLabels, setCounterLabels] = useState({...initialFieldsState})

  const [isOpened, setIsOpened] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isSettingCreated, setIsSettingCreated] = useState(false);


  useEffect(() => {
    if(commentsCounter || isSettingCreated) {
      setCounterLabels({
        zeroCommentsLabel: commentsCounter.zeroCommentsLabel,
        singleCommentLabel: commentsCounter.singleCommentLabel,
        multipleCommentsLabel: commentsCounter.multipleCommentsLabel
      })
      setIsSettingCreated(false)
    }
  },[isSettingCreated])

  const initialRequestData = useMemo(() => {
    const requestData = {
      type: 'comments-counters',
      label: 'Comments counter'
    }
    if(commentsCounter?.uuid) requestData.configurationUUID = commentsCounter?.uuid
    return requestData;
  }, [commentsCounter])

  const toggleSwitch = useCallback( async() => {
    if(commentsCounter?.uuid) {
      const response = await dispatch(updateAddOnsSetting({
        ...initialRequestData,
        data: {
          ...commentsCounter, 
          isCommentsCounterEnabled: !commentsCounter.isCommentsCounterEnabled
        }
      }))
      if(response.payload) setIsOpened(false)
    } else {
      const response = await dispatch(createAddOnsSetting({
        ...initialRequestData,
        data: {
          ...commentsCounter, 
          isCommentsCounterEnabled: !commentsCounter.isCommentsCounterEnabled,
          commentingUnitConfigurationUUID
        }
      }))
      if(response.payload) setIsSettingCreated(true)
    }
  }, [commentsCounter, dispatch])


  const isChanged = useMemo(() => {
    const labels = {
      zeroCommentsLabel: commentsCounter.zeroCommentsLabel,
      singleCommentLabel: commentsCounter.singleCommentLabel,
      multipleCommentsLabel: commentsCounter.multipleCommentsLabel
    }
    const isFieldsChanged = !(JSON.stringify(labels) === JSON.stringify(counterLabels));
    return isFieldsChanged
  }, [counterLabels, commentsCounter])

  const disableSave = useMemo(() => !counterLabels.zeroCommentsLabel || !counterLabels.singleCommentLabel || !counterLabels.multipleCommentsLabel ,[counterLabels])

  const handleCounterLabelChange = (value, label)=> {
    setCounterLabels({
      ...counterLabels,
      [label]: value
    })
  }

  const handleCancel = () => {
    setIsOpened(false)
    setCounterLabels({
      zeroCommentsLabel: commentsCounter.zeroCommentsLabel,
      singleCommentLabel: commentsCounter.singleCommentLabel,
      multipleCommentsLabel: commentsCounter.multipleCommentsLabel
    })
  }

  const handleSave = useCallback(async() => {
    setIsProcessing(true);
    const response = await dispatch(updateAddOnsSetting({
      ...initialRequestData,
      data: {
        ...commentsCounter,
        ...counterLabels
      }
    }))
    if(response.payload) setIsOpened(false)
    setIsProcessing(false);
  })

  const controller = () => (
    <AddOnController
      title={'Comments Counter'}
      image={'illustration-comments-counter.svg'}
      description={`Comments counter allows users to immediately see the number 
      of comments on a particular article. Comments counter can be placed 
      anywhere on the page with a fully customizable design.`}
      isOpened={isOpened}
      setIsOpened={setIsOpened}
      toggle={commentsCounter?.isCommentsCounterEnabled}
      toggleClick={toggleSwitch}
    />
  );

  return (
    <ExpandableSection
      className={`commentscountersection ${props.classes.commentscountersection}`}
      controller={controller()}
      isOpened={isOpened}
      setIsOpened={setIsOpened}
      handleCancel={handleCancel}
      handleSave={handleSave}
      isProcessing={isProcessing}
      isChanged={isChanged}
      disableSave={disableSave}
    >
      <SectionCard>
        <SectionGroup>
            <SectionGroupDescription />
            <CopySnippetField 
                title={'Comments Counter Loading Script'} 
                description={
                  <div>
                    Add the following script to the bottom of the page where you’d like to display the Comments Counter, 
                    right before the closing <strong>{'</body>'}</strong>:
                  </div>
                } 
                codeSnippet={commentsCounter?.headerCode} 
            />
        </SectionGroup>

        <SectionGroup>
          <SectionGroupDescription />
            <CopySnippetField 
              title={'Comments Counter Code'} 
              description={<div>Add this suffix (anchor) to links where Comments Counter should appear:</div>} 
              codeSnippet={commentsCounter?.bodyCode} 
            />
            <InlineMessage 
              name={'commentscountercode'} 
              variant={'neutral'} 
              icon={'exclamation-triangle'} 
              message={
              <div>
                Make sure you replace <strong>&#123;articleURL&#125;</strong> with the link to your specific page
                { !isPageIDEnabled ? '.' : 
                    <span>, and <strong>&#123;extPageId&#125;</strong> with the proper External Page ID value for the specific page.</span>
                }
              </div>
              } 
            />
        </SectionGroup>
      </SectionCard>

      <SectionCard>
        <SectionGroup>
          <SectionGroupDescription 
            title='Label' 
            description={<div>Customize the <strong>comments counter label</strong> for various scenarios:</div>} 
          />
            <Grid name={'commentscounterlabels'}>
              <GridSet direction={'horizontal'}>
                <GridItem>
                  <TextField
                    name={'zeroCommentLabel'} 
                    type={'text'} 
                    label={'Zero Comment Label'}
                    placeholder={'Comments'}
                    value={counterLabels.zeroCommentsLabel}
                    valid={!!counterLabels.zeroCommentsLabel}
                    handleChange={e => handleCounterLabelChange(e.target.value, 'zeroCommentsLabel')}
                    required={true}
                  />
                </GridItem>
                <GridItem>
                  <TextField
                    name={'singleCommentLabel'} 
                    type={'text'} 
                    label={'Single Comment Label'}
                    placeholder={'1 Comment'}
                    value={counterLabels.singleCommentLabel}
                    valid={!!counterLabels.singleCommentLabel}
                    handleChange={e => handleCounterLabelChange(e.target.value, 'singleCommentLabel')}
                    required={true}
                  />
                </GridItem>
                <GridItem>
                  <TextField
                    name={'multipleCommentsLabel'} 
                    type={'text'} 
                    label={'Multiple Comments Label'}
                    placeholder={'{count} Comments'}
                    value={counterLabels.multipleCommentsLabel}
                    valid={!!counterLabels.multipleCommentsLabel}
                    handleChange={e => handleCounterLabelChange(e.target.value, 'multipleCommentsLabel')}
                    required={true}
                  />
                </GridItem>
              </GridSet>
              </Grid>
        </SectionGroup>
      </SectionCard>
    </ExpandableSection>
  );
}

/* ++++++++++ --------------- EXPORTS --------------- ++++++++++ */
export default injectSheet(styles)(CommentsCounterSection)